import { useEffect, useMemo, useState } from 'react';
import { Shell } from '../Shell';
import { useAsync } from '../../core/useAsync';
import {
  ApiError, cachedLeague, getMe, getPresets, getProposals, proposeRound, withdrawProposal,
} from '../../core/api';
import { summarise } from '../presets';
import { paceLabel, closesIn } from '../format';
import type { LeagueInfo, Me, MediaType, Pace, Preset, Proposal } from '../../core/types';
import s from './ProposeRound.module.css';

// A member proposes a round into a Jukebox season: category, media, a scoring preset,
// veto, pace — capped per member, drawn into the running order later. Wired to
// /api/rounds/propose (+ /api/presets, /api/league). Reads target the newest season
// server-side, so no season_id is needed here.

const PACES: { key: Pace; label: string; note: string }[] = [
  { key: 'rapid', label: 'Rapid', note: '24h / phase' },
  { key: 'steady', label: 'Steady', note: '3 days / phase' },
  { key: 'slow', label: 'Slow', note: '1 week / phase' },
];

export function ProposeRound() {
  const me = useAsync<Me>(getMe, []);
  const league = useAsync<LeagueInfo>(cachedLeague, []);
  const presets = useAsync<Preset[]>(getPresets, []);
  const proposals = useAsync<Proposal[]>(getProposals, []);

  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const [media, setMedia] = useState<MediaType>('audio');
  const [preset, setPreset] = useState<number | null>(null);
  const [veto, setVeto] = useState(true);
  const [pace, setPace] = useState<Pace>('steady');
  const [busy, setBusy] = useState<number | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const allowedMedia = league.data?.allowed_media?.length ? league.data.allowed_media : (['audio', 'video', 'image'] as MediaType[]);
  const allowedPaces = league.data?.allowed_paces?.length ? league.data.allowed_paces : (['rapid', 'steady', 'slow'] as Pace[]);
  const vetoPolicy = league.data?.veto_policy;
  const vetoForced = vetoPolicy === 'on' || vetoPolicy === 'off';
  const vetoValue = vetoPolicy === 'on' ? true : vetoPolicy === 'off' ? false : veto;

  // Picking a preset also seeds the veto toggle from that preset's rule (unless the
  // league forces veto on/off) — so the toggle tracks the scoring you chose.
  function pickPreset(p: Preset) {
    setPreset(p.ruleset_id);
    if (!vetoForced) setVeto(p.veto_enabled);
  }

  // Seed the form's defaults from the league's allowed-sets / presets once loaded.
  useEffect(() => { if (league.data && !allowedMedia.includes(media)) setMedia(allowedMedia[0]); }, [league.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (league.data && !allowedPaces.includes(pace)) setPace(allowedPaces[0]); }, [league.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (presets.data && preset == null && presets.data.length) pickPreset(presets.data[0]); }, [presets.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const myId = me.data?.user?.user_id ?? null;
  const isAdmin = me.data?.user?.is_admin ?? false;
  const mine = useMemo(() => (proposals.data ?? []).filter((p) => p.proposed_by === myId), [proposals.data, myId]);

  async function submit() {
    if (!category.trim() || preset == null || busy != null) return;
    setBusy('new'); setErr(null); setMsg(null);
    try {
      await proposeRound({ category: category.trim(), note: note.trim() || undefined, media_type: media, preset_id: preset, veto: vetoValue, pace });
      setCategory('');
      setNote('');
      setMsg('Proposed. It’ll be drawn into the running order when proposals close.');
      proposals.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not submit your proposal.');
    } finally { setBusy(null); }
  }

  async function withdraw(id: number) {
    if (busy != null || !window.confirm('Withdraw this proposal?')) return;
    setBusy(id); setErr(null); setMsg(null);
    try { await withdrawProposal(id); proposals.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not withdraw.'); }
    finally { setBusy(null); }
  }

  const cap = league.data?.proposals_per_member ?? null;
  const atCap = cap != null && mine.length >= cap;
  const drawn = league.data?.proposals_state === 'drawn';
  const deadlineTxt = closesIn(league.data?.proposals_deadline);

  if (me.loading || league.loading) return <Shell><div className={s.state}>Loading…</div></Shell>;

  // Only meaningful in Jukebox mode — in DJ mode the owner builds every round.
  if (league.data && league.data.league_type !== 'jukebox') {
    return (
      <Shell>
        <div className={s.appbar}><span className={s.ctx}>Propose a round</span></div>
        <div className={s.state}>This league isn’t in Jukebox mode — the owner builds the rounds. Ask them to switch to Jukebox in League settings if you’d like to propose.</div>
      </Shell>
    );
  }

  // Proposals have been drawn into the running order — the window is closed.
  if (drawn) {
    return (
      <Shell>
        <div className={s.appbar}><span className={s.ctx}>Propose a round</span></div>
        <div className={s.state}>Proposals are closed — the running order’s been drawn. Your rounds are on the schedule now.</div>
      </Shell>
    );
  }

  const noPresets = !presets.loading && (presets.data?.length ?? 0) === 0;

  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>Propose a round</span>
        <span className={s.used}>{mine.length}{cap != null ? ` of ${cap}` : ''} used{deadlineTxt ? ` · ${deadlineTxt}` : ''}</span>
      </div>

      {err ? <div className={s.banner} style={{ color: 'var(--video)' }}>{err}</div> : null}
      {msg ? <div className={s.banner} style={{ color: 'var(--good)' }}>{msg}</div> : null}

      {noPresets ? (
        <div className={s.state}>No scoring presets yet. The owner needs to add at least one in <b>Admin → Scoring presets</b> before rounds can be proposed.</div>
      ) : (
        <div className={s.form}>
          <div className={s.field}>
            <label>Category <span className={s.cnt}>{category.length} / 120</span></label>
            <input className={s.tin} maxLength={120} value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Songs about trains" />
          </div>

          <div className={s.field}>
            <label>Description <span className={s.cnt}>optional · {note.length} / 140</span></label>
            <input className={s.tin} maxLength={140} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Actual trains only — no metaphors" />
          </div>

          <div className={s.field}>
            <label>Media type</label>
            <div className={s.seg}>
              {allowedMedia.map((m) => (
                <button key={m} className={m === media ? s.on : ''} onClick={() => setMedia(m)}>
                  <span className={s.m} style={{ background: `var(--${m})` }} />{m}
                </button>
              ))}
            </div>
          </div>

          <div className={s.field}>
            <label>Scoring preset</label>
            <div className={s.presets}>
              {(presets.data ?? []).map((p) => (
                <button key={p.ruleset_id} className={`${s.pcard}${preset === p.ruleset_id ? ' ' + s.on : ''}`} onClick={() => pickPreset(p)}>
                  <span className={s.pt}><b>{p.name}</b><span className={s.rad} /></span>
                  <small>{summarise(p)}</small>
                </button>
              ))}
            </div>
          </div>

          {allowedPaces.length > 1 ? (
            <div className={s.field}>
              <label>Pace</label>
              <div className={s.seg}>
                {PACES.filter((p) => allowedPaces.includes(p.key)).map((p) => (
                  <button key={p.key} className={pace === p.key ? s.on : ''} style={{ flexDirection: 'column', gap: 1, lineHeight: 1.15 }} onClick={() => setPace(p.key)}>
                    {p.label}<small style={{ fontSize: 10, opacity: 0.65, fontWeight: 500 }}>{p.note}</small>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className={s.field}>
            <label>Veto</label>
            <div className={s.switchrow}>
              <div className={s.t}>
                <b>{vetoValue ? 'Veto on' : 'Veto off'}</b>
                <small>{vetoForced ? 'Set by the league — can’t change per round' : vetoValue ? 'Players can send one track down' : 'No veto this round'}</small>
              </div>
              <button className={`${s.sw}${vetoValue ? '' : ' ' + s.off}`} disabled={vetoForced} onClick={() => setVeto((v) => !v)} aria-label="Toggle veto" aria-pressed={vetoValue} />
            </div>
          </div>
        </div>
      )}

      {mine.length || (proposals.data?.length ?? 0) ? (
        <>
          <div className={s.sec}>Proposals so far</div>
          <div className={s.plist}>
            {(proposals.data ?? []).map((p) => {
              const own = p.proposed_by === myId;
              return (
                <div key={p.round_id} className={s.prow}>
                  <span className={s.pdot} style={{ background: `var(--${p.media_type})` }} />
                  <span className={s.pinfo}>
                    <b>{p.category}</b>
                    {p.note ? <span className={s.pnote}>{p.note}</span> : null}
                    <small>{p.media_type} · {p.preset_name}{p.veto ? ' · veto' : ''} · by {own ? 'you' : p.proposed_by_name}</small>
                  </span>
                  {own || isAdmin ? (
                    <button className={s.withdraw} disabled={busy != null} onClick={() => withdraw(p.round_id)}>{busy === p.round_id ? '…' : 'Withdraw'}</button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {noPresets ? null : (
        <div className={s.foot}>
          <button className={s.submit} disabled={!category.trim() || preset == null || busy != null || atCap} onClick={submit}>{atCap ? `Limit reached (${cap})` : busy === 'new' ? 'Proposing…' : `Propose${pace ? ` · ${paceLabel(pace)}` : ''}`}</button>
        </div>
      )}
    </Shell>
  );
}
